import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Stack,
	Typography,
} from "@mui/material";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useCreateExtractionConfigurationMutation } from "Configurations/Api/Mutations/useCreateExtractionConfigurationMutation";
import { useExtractionConfigurationsQuery } from "Configurations/Api/Queries/useExtractionConfigurationsQuery";
import type { ImportedConfig } from "Configurations/Types/ImportedConfig";
import { useConnectorConfigurationsQuery } from "ConnectorConfigurations/Api/Queries/useConnectorConfigurationsQuery";
import { useTenantsQuery } from "Tenants/Api/Queries/useTenantsQuery";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type Props = {
	open: boolean;
	onClose: () => void;
	configurations: ImportedConfig[];
};

export const ImportConfigurationsDialog: FC<Props> = ({
	open,
	onClose,
	configurations,
}) => {
	const [isImporting, setIsImporting] = useState(false);
	const [skipDuplicates, setSkipDuplicates] = useState(true);
	const createMutation = useCreateExtractionConfigurationMutation();
	const existingConfigsQuery = useExtractionConfigurationsQuery();
	const connectorConfigsQuery = useConnectorConfigurationsQuery();
	const userInfoQuery = useUserInfoQuery();

	const isAdmin =
		userInfoQuery.data?.status === 200 &&
		userInfoQuery.data.data.accessRightCodes?.includes("Tenants");

	const tenantsQuery = useTenantsQuery(isAdmin && open);

	const connectorConfigs = connectorConfigsQuery.data?.items ?? [];
	const tenants = tenantsQuery.data?.items ?? [];
	const existingConfigs = existingConfigsQuery.data?.items ?? [];

	// Detect duplicates by name AND tenant (name must be unique within a tenant)
	// Only consider active configurations as duplicates (isActive = true)
	// Soft-deleted configs (isActive = false) don't block imports
	const { duplicates, newConfigs } = useMemo(() => {
		// Create a map of existing ACTIVE configs: "name|tenantCode" -> config
		const existingConfigsMap = new Map(
			existingConfigs
				.filter((c) => c.isActive !== false) // Only consider active configs
				.map((c) => [
					`${c.name.toLowerCase()}|${(c.tenantCode || "").toLowerCase()}`,
					c,
				]),
		);

		const duplicates = configurations.filter((c) => {
			const key = `${c.name.toLowerCase()}|${(c.tenantCode || "").toLowerCase()}`;
			return existingConfigsMap.has(key);
		});

		const newConfigs = configurations.filter((c) => {
			const key = `${c.name.toLowerCase()}|${(c.tenantCode || "").toLowerCase()}`;
			return !existingConfigsMap.has(key);
		});

		return { duplicates, newConfigs };
	}, [configurations, existingConfigs]);

	const handleImport = async () => {
		setIsImporting(true);
		let successCount = 0;
		let failureCount = 0;
		let skippedCount = 0;

		// Determine which configs to import
		const configsToImport = skipDuplicates ? newConfigs : configurations;

		if (skipDuplicates && duplicates.length > 0) {
			skippedCount = duplicates.length;
		}

		for (const config of configsToImport) {
			try {
				// Find connector configuration by provider name
				let connectorConfigId: number | undefined;
				if (config.connectorProvider) {
					const connectorConfig = connectorConfigs.find(
						(cc) => cc.provider === config.connectorProvider,
					);
					connectorConfigId = connectorConfig?.connectorConfigurationID;
				}

				if (!connectorConfigId && connectorConfigs.length > 0) {
					// Default to first connector if not found
					connectorConfigId = connectorConfigs[0].connectorConfigurationID;
				}

				if (!connectorConfigId) {
					toast.error(`No connector configuration found for ${config.name}`);
					failureCount++;
					continue;
				}

				// Find tenant by code
				let tenantId: number | null = null;
				if (config.tenantCode && isAdmin) {
					const tenant = tenants.find((t) => t.code === config.tenantCode);
					tenantId = tenant?.tenantID ?? null;
				}

				await createMutation.mutateAsync({
					name: config.name,
					prompt: config.prompt,
					jsonSchema: config.jsonSchema,
					connectorConfigurationId: connectorConfigId,
					pageLimit: config.pageLimit,
					forceOcr: config.forceOcr ?? true,
					identificationHints: config.identificationHints,
					tenantId: tenantId,
				});

				successCount++;
			} catch (error) {
				console.error(`Failed to import ${config.name}:`, error);
				failureCount++;
			}
		}

		setIsImporting(false);

		// Show appropriate messages
		const messages = [];
		if (successCount > 0) {
			messages.push(`Successfully imported ${successCount}`);
		}
		if (skippedCount > 0) {
			messages.push(`Skipped ${skippedCount} duplicate(s)`);
		}
		if (failureCount > 0) {
			messages.push(`Failed ${failureCount}`);
		}

		if (messages.length > 0) {
			const fullMessage = messages.join(", ");
			if (failureCount > 0) {
				toast.warning(fullMessage);
			} else {
				toast.success(fullMessage);
			}
		}

		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Import Configurations</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Alert severity="info">
						You are about to import {configurations.length} configuration(s).
						Review the list below and confirm to proceed.
					</Alert>

					{duplicates.length > 0 && (
						<Alert severity="warning">
							Found {duplicates.length} configuration(s) with names that already
							exist.{" "}
							{skipDuplicates
								? "These will be skipped."
								: "These will create duplicates with conflicting names."}
						</Alert>
					)}

					<FormControlLabel
						control={
							<Checkbox
								checked={skipDuplicates}
								onChange={(e) => setSkipDuplicates(e.target.checked)}
							/>
						}
						label={`Skip configurations with duplicate names (${duplicates.length})`}
					/>

					<Box>
						<Typography variant="subtitle2" gutterBottom>
							{skipDuplicates
								? `New Configurations (${newConfigs.length}):`
								: `All Configurations (${configurations.length}):`}
						</Typography>
						<Stack spacing={1} sx={{ maxHeight: 400, overflowY: "auto" }}>
							{(skipDuplicates ? newConfigs : configurations).map(
								(config, index) => {
									const isDuplicate = duplicates.some(
										(d) => d.name === config.name,
									);
									return (
										<Box
											key={index}
											sx={{
												p: 2,
												border: "1px solid",
												borderColor: isDuplicate ? "warning.main" : "divider",
												borderRadius: 1,
												bgcolor: isDuplicate
													? "warning.lighter"
													: "transparent",
											}}
										>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center"
												mb={1}
											>
												<Typography variant="body1" fontWeight="bold">
													{config.name}
												</Typography>
												{isDuplicate && (
													<Chip
														label="Duplicate"
														size="small"
														color="warning"
													/>
												)}
												{config.tenantCode && (
													<Chip
														label={config.tenantCode}
														size="small"
														color="primary"
													/>
												)}
												{config.connectorProvider && (
													<Chip
														label={config.connectorProvider}
														size="small"
														color="secondary"
													/>
												)}
											</Stack>
											<Typography variant="caption" color="text.secondary">
												Prompt: {config.prompt.substring(0, 100)}
												{config.prompt.length > 100 ? "..." : ""}
											</Typography>
										</Box>
									);
								},
							)}
						</Stack>
					</Box>

					{connectorConfigs.length === 0 && (
						<Alert severity="warning">
							No connector configurations found. You need at least one connector
							configuration to import extraction configurations.
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isImporting}>
					Cancel
				</Button>
				<Button
					onClick={handleImport}
					variant="contained"
					color="primary"
					disabled={
						isImporting ||
						connectorConfigs.length === 0 ||
						(skipDuplicates && newConfigs.length === 0)
					}
				>
					{isImporting
						? "Importing..."
						: `Import ${skipDuplicates ? newConfigs.length : configurations.length}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
